import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  BarChart3, BookOpen,
  Brain, Mic, Code, ClipboardList, TrendingUp,
  AlertTriangle, Star, Lightbulb, ListChecks, Award,
  Users, GraduationCap,
} from "lucide-react";
import {
  RadialBarChart, RadialBar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import { AppButton, BackButton } from "../ui";
import apiClient from "../../utils/apiClient";
import { useTokenColors } from "../../utils/themeColor";
import "./aiReport.scss";

// ── Score colour helpers ─────────────────────────────────────────────────────
// They return a TOKEN NAME, not a colour. Two reasons: the tints below were
// built by concatenating an alpha onto a hex (`${color}15`), which only works on
// a literal; and one of these feeds a Recharts `fill`, an SVG presentation
// attribute where var() does not resolve — so that call site resolves the token
// through themeColor.js instead (see ScoreGauge).
const scoreToken = (score) => {
  if (score >= 75) return "--success";
  if (score >= 50) return "--warning";
  return "--danger";
};

const ratingToken = (rating) =>
  ({
    Excellent: "--success",
    Good: "--info",
    Average: "--warning",
    "Below Average": "--chart-6",
    Poor: "--danger",
  }[rating] || "--muted-foreground");

const gradeToken = (grade) => {
  if (grade === "A+" || grade === "A") return "--success";
  if (grade === "B+" || grade === "B") return "--info";
  if (grade === "C") return "--warning";
  return "--danger";
};

/** A token name → the CSS colour, optionally at an alpha. */
const tone = (token, alpha) =>
  alpha === undefined ? `hsl(var(${token}))` : `hsl(var(${token}) / ${alpha})`;

// ── Radial score widget ──
function ScoreGauge({ score, grade, size = 180 }) {
  // `fill` is an SVG presentation attribute, so it takes a resolved colour.
  const c = useTokenColors({ score: scoreToken(score), track: "--bg-tertiary" });
  const data = [{ name: "score", value: score, fill: c.score }];
  return (
    <div className="air-score-gauge">
      <ResponsiveContainer width={size} height={size}>
        <RadialBarChart
          cx="50%" cy="50%" innerRadius="70%" outerRadius="100%"
          startAngle={180} endAngle={0} data={data}
        >
          <RadialBar dataKey="value" cornerRadius={10} background={{ fill: c.track }} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="air-score-gauge__label">
        <span className="air-score-gauge__number" style={{ color: tone(scoreToken(score)) }}>{score}%</span>
        <span className="air-score-gauge__grade" style={{ color: tone(gradeToken(grade)) }}>Grade {grade}</span>
      </div>
    </div>
  );
}

// ── Section card ──
function SectionCard({ icon: Icon, title, rating, token, children }) {
  return (
    <div className="air-section-card">
      <div className="air-section-card__header">
        <div className="air-section-card__icon" style={{ backgroundColor: tone(token, 0.08), color: tone(token) }}>
          <Icon size={18} />
        </div>
        <h3 className="air-section-card__title">{title}</h3>
        {rating && (
          <span className="air-section-card__rating" style={{ color: tone(ratingToken(rating)), backgroundColor: tone(ratingToken(rating), 0.07) }}>
            {rating}
          </span>
        )}
      </div>
      <div className="air-section-card__body">{children}</div>
    </div>
  );
}

// ── Stat row ──
function StatRow({ label, value, suffix, token }) {
  return (
    <div className="air-stat-row">
      <span className="air-stat-row__label">{label}</span>
      <span className="air-stat-row__value" style={token ? { color: tone(token) } : {}}>
        {value}{suffix && <small className="air-stat-row__suffix">{suffix}</small>}
      </span>
    </div>
  );
}

// ── Progress bar ──
function ProgressBar({ value, max = 100, token = "--info", label }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div className="air-progress">
      {label && <div className="air-progress__label">{label} <span>{pct}%</span></div>}
      <div className="air-progress__track">
        <div className="air-progress__fill" style={{ width: `${pct}%`, backgroundColor: tone(token) }} />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════
// Main AIReportPage Component
// ═════════════════════════════════════════
const AIReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  // Five report sections on the summary chart — categories, so the chart ramp.
  // Resolved rather than written as `hsl(var(…))` because Recharts sets `fill`
  // as an SVG presentation attribute, where var() does not resolve.
  const chart = useTokenColors({
    attendance: "--chart-5",
    assignments: "--chart-1",
    practice: "--chart-2",
    ai: "--chart-3",
    mock: "--chart-6",
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const { data } = await apiClient.get(`/ai-report/${reportId}`);
        setReport(data);
      } catch {
        setErr("Failed to load report.");
      }
      setLoading(false);
    };
    fetchReport();
  }, [reportId]);

  if (loading) {
    return (
      <div className="air-page air-page--loading">
        <Spinner animation="border" variant="primary" />
        <span>Loading AI Report...</span>
      </div>
    );
  }

  if (err || !report) {
    return (
      <div className="air-page air-page--loading">
        <AlertTriangle size={32} className="text-danger" />
        <span>{err || "Report not found."}</span>
        <AppButton variant="outline" size="sm" onClick={() => navigate(-1)}>Go Back</AppButton>
      </div>
    );
  }

  const r = report;

  // Category scores for bar chart
  // Coding Practice is omitted server-side when the feature is off for this
  // student (plan / institute / all their courses) → hide its slice + section.
  const codingOn = !!r.codingPractice;
  const categoryData = [
    { name: "Attendance", score: r.attendance?.percent || 0, fill: chart.attendance },
    { name: "Assignments", score: Math.round(((r.assignments?.codingAssignments?.completionRate || 0) + (r.assignments?.generalAssignments?.completionRate || 0)) / 2) || 0, fill: chart.assignments },
    ...(codingOn ? [{ name: "Practice", score: r.codingPractice?.avgScore || 0, fill: chart.practice }] : []),
    { name: "AI Learning", score: r.aiLearning?.passRate || 0, fill: chart.ai },
    { name: "Mock Int.", score: r.mockInterview?.avgScore || 0, fill: chart.mock },
    { name: "Quizzes", score: r.quizzes?.avgScore || 0, fill: "#06b6d4" },
  ].filter(d => d.score > 0 || true); // show all

  return (
    <div className="air-page">
      {/* Header */}
      <div className="air-header">
        <BackButton />
        <div className="air-header__info">
          <h1 className="air-header__title">
            <BarChart3 size={22} /> AI Performance Report
          </h1>
          <p className="air-header__meta">
            {r.student?.name} &middot; {r.courseInfo?.courseName || "—"} &middot; Generated {new Date(r.generatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </p>
        </div>
      </div>

      {/* ── Top score + summary area ── */}
      <div className="air-top-grid">
        <div className="air-score-card">
          <ScoreGauge score={r.overallScore} grade={r.grade} />
          <div className="air-score-card__info">
            <h2>Overall Performance</h2>
            <p className="air-score-card__summary">{r.aiSummary}</p>
          </div>
        </div>
        <div className="air-chart-card">
          <h3 className="air-chart-card__title">Category Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={categoryData} layout="vertical" margin={{ left: 10, right: 10 }}>
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={85} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={18}>
                {categoryData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Detailed section cards ── */}
      <div className="air-sections-grid">

        {/* Per-course breakdown (multi-course) */}
        {r.courses?.length > 1 ? (
          r.courses.map((c, idx) => (
            <SectionCard key={idx} icon={GraduationCap} title={c.courseName || `Course ${idx + 1}`} token="--chart-1">
              <StatRow label="Batch" value={c.batchName || "N/A"} />
              <ProgressBar value={c.courseCompletion} token="--chart-1" label="Course Completion" />
              <h4 className="air-sub-title" style={{ marginTop: 10 }}>Attendance</h4>
              <ProgressBar value={c.attendance?.percent} token="--info" label="Attendance Rate" />
              <div className="air-stat-grid">
                <StatRow label="Present" value={c.attendance?.present || 0} token="--success" />
                <StatRow label="Partial" value={c.attendance?.partial || 0} token="--warning" />
                <StatRow label="Absent" value={c.attendance?.absent || 0} token="--danger" />
                <StatRow label="Sessions" value={c.attendance?.totalSessions || 0} />
              </div>
              <h4 className="air-sub-title" style={{ marginTop: 10 }}>Coding Assignments</h4>
              <ProgressBar value={c.assignments?.codingAssignments?.completionRate} token="--warning" label="Completion" />
              <div className="air-stat-grid">
                <StatRow label="Attempted" value={`${c.assignments?.codingAssignments?.attempted || 0}/${c.assignments?.codingAssignments?.total || 0}`} />
                <StatRow label="Passed" value={c.assignments?.codingAssignments?.passedAnswers || 0} token="--success" />
                <StatRow label="Failed" value={c.assignments?.codingAssignments?.failedAnswers || 0} token="--danger" />
              </div>
              <h4 className="air-sub-title" style={{ marginTop: 10 }}>General Assignments</h4>
              <ProgressBar value={c.assignments?.generalAssignments?.completionRate} token="--warning" label="Completion" />
              <div className="air-stat-grid">
                <StatRow label="Submitted" value={`${c.assignments?.generalAssignments?.submitted || 0}/${c.assignments?.generalAssignments?.total || 0}`} />
                <StatRow label="On Time" value={c.assignments?.generalAssignments?.onTime || 0} token="--success" />
                <StatRow label="Late" value={c.assignments?.generalAssignments?.late || 0} token="--danger" />
              </div>
            </SectionCard>
          ))
        ) : (
          <>
            {/* Attendance (single course) */}
            <SectionCard icon={Users} title="Class Attendance" rating={r.attendance?.rating} token="--info">
              <ProgressBar value={r.attendance?.percent} token="--info" label="Attendance Rate" />
              <div className="air-stat-grid">
                <StatRow label="Present" value={r.attendance?.present || 0} token="--success" />
                <StatRow label="Partial" value={r.attendance?.partial || 0} token="--warning" />
                <StatRow label="Absent" value={r.attendance?.absent || 0} token="--danger" />
                <StatRow label="Total Sessions" value={r.attendance?.totalSessions || 0} />
                <StatRow label="Active Minutes" value={r.attendance?.totalActiveMinutes || 0} suffix=" min" />
              </div>
            </SectionCard>

            {/* Course Progress (single course) */}
            <SectionCard icon={GraduationCap} title="Course Progress" token="--chart-1">
              <ProgressBar value={r.courseInfo?.courseCompletion} token="--chart-1" label="Completion" />
              <div className="air-stat-grid">
                <StatRow label="Completed" value={r.courseInfo?.completedSessions || 0} />
                <StatRow label="Total Sessions" value={r.courseInfo?.totalSessions || 0} />
                <StatRow label="Batch" value={r.courseInfo?.batchName || "N/A"} />
              </div>
            </SectionCard>

            {/* Assignments (single course) */}
            <SectionCard icon={ClipboardList} title="Assignments" rating={r.assignments?.rating} token="--warning">
              <h4 className="air-sub-title">Coding Assignments</h4>
              <ProgressBar value={r.assignments?.codingAssignments?.completionRate} token="--warning" label="Completion" />
              <div className="air-stat-grid">
                <StatRow label="Attempted" value={`${r.assignments?.codingAssignments?.attempted || 0}/${r.assignments?.codingAssignments?.total || 0}`} />
                <StatRow label="Passed" value={r.assignments?.codingAssignments?.passedAnswers || 0} token="--success" />
                <StatRow label="Failed" value={r.assignments?.codingAssignments?.failedAnswers || 0} token="--danger" />
              </div>
              <h4 className="air-sub-title" style={{ marginTop: 12 }}>General Assignments</h4>
              <ProgressBar value={r.assignments?.generalAssignments?.completionRate} token="--warning" label="Completion" />
              <div className="air-stat-grid">
                <StatRow label="Submitted" value={`${r.assignments?.generalAssignments?.submitted || 0}/${r.assignments?.generalAssignments?.total || 0}`} />
                <StatRow label="On Time" value={r.assignments?.generalAssignments?.onTime || 0} token="--success" />
                <StatRow label="Late" value={r.assignments?.generalAssignments?.late || 0} token="--danger" />
                <StatRow label="Avg Marks" value={r.assignments?.generalAssignments?.avgMarks || 0} />
              </div>
            </SectionCard>
          </>
        )}

        {/* Coding Practice — hidden when the feature is off for this student */}
        {codingOn && (
          <SectionCard icon={Code} title="Coding Practice" rating={r.codingPractice?.rating} token="--success">
            <ProgressBar
              value={r.codingPractice?.solved}
              max={r.codingPractice?.totalQuestions || 1}
              token="--success"
              label="Solved"
            />
            <div className="air-stat-grid">
              <StatRow label="Solved" value={r.codingPractice?.solved || 0} token="--success" />
              <StatRow label="Partial" value={r.codingPractice?.partiallySolved || 0} token="--warning" />
              <StatRow label="Attempted Only" value={r.codingPractice?.attempted || 0} />
              <StatRow label="Total Attempts" value={r.codingPractice?.totalAttempts || 0} />
              <StatRow label="Avg Score" value={`${r.codingPractice?.avgScore || 0}%`} />
            </div>
          </SectionCard>
        )}

        {/* AI Learning */}
        <SectionCard icon={Brain} title="Learn with AI" rating={r.aiLearning?.rating} token="--chart-1">
          <ProgressBar value={r.aiLearning?.passRate} token="--chart-1" label="Pass Rate" />
          <div className="air-stat-grid">
            <StatRow label="Questions" value={r.aiLearning?.totalQuestions || 0} />
            <StatRow label="Total Attempts" value={r.aiLearning?.totalAttempts || 0} />
            <StatRow label="Avg Score" value={`${r.aiLearning?.avgScore || 0}%`} />
            <StatRow label="Passed" value={r.aiLearning?.passed || 0} token="--success" />
            <StatRow label="Failed" value={r.aiLearning?.failed || 0} token="--danger" />
          </div>
        </SectionCard>

        {/* Mock Interviews */}
        <SectionCard icon={Mic} title="Mock Interviews" rating={r.mockInterview?.rating} token="--chart-6">
          <div className="air-stat-grid">
            <StatRow label="Completed" value={r.mockInterview?.totalInterviews || 0} />
            <StatRow label="Avg Score" value={`${r.mockInterview?.avgScore || 0}%`} />
            <StatRow label="Highest" value={`${r.mockInterview?.highestScore || 0}%`} token="--success" />
            <StatRow label="Questions" value={r.mockInterview?.totalQuestionAnswered || 0} />
          </div>
          {r.mockInterview?.strongAreas?.length > 0 && (
            <div className="air-areas">
              <span className="air-areas__label air-areas__label--strong">Strong:</span>
              {r.mockInterview.strongAreas.map((a, i) => (
                <span key={i} className="air-areas__tag air-areas__tag--strong">{a}</span>
              ))}
            </div>
          )}
          {r.mockInterview?.weakAreas?.length > 0 && (
            <div className="air-areas">
              <span className="air-areas__label air-areas__label--weak">Weak:</span>
              {r.mockInterview.weakAreas.map((a, i) => (
                <span key={i} className="air-areas__tag air-areas__tag--weak">{a}</span>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Quizzes */}
        {r.quizzes?.totalAttempts > 0 && (
          <SectionCard icon={Award} title="Quizzes & Assessments" rating={r.quizzes?.rating} token="--chart-5">
            <ProgressBar value={r.quizzes?.passRate} token="--chart-5" label="Pass Rate" />
            <div className="air-stat-grid">
              <StatRow label="Attempts" value={r.quizzes?.totalAttempts || 0} />
              <StatRow label="Avg Score" value={`${r.quizzes?.avgScore || 0}%`} />
            </div>
          </SectionCard>
        )}

        {/* Self-Paced Courses */}
        {r.selfPaced?.length > 0 && (
          <SectionCard icon={BookOpen} title="Self-Paced Courses" token="--chart-2">
            {r.selfPaced.map((sp, i) => (
              <div key={i} className="air-self-paced-item">
                <ProgressBar
                  value={sp.completionPct}
                  token="--chart-2"
                  label={sp.courseName}
                />
                <div className="air-stat-row">
                  <span className="air-stat-row__label">Lessons</span>
                  <span className="air-stat-row__value">{sp.lessonsCompleted}/{sp.totalLessons}</span>
                </div>
              </div>
            ))}
          </SectionCard>
        )}
      </div>

      {/* ── AI Insights section ── */}
      <div className="air-insights">
        <h2 className="air-insights__title"><TrendingUp size={20} /> AI Insights & Recommendations</h2>

        <div className="air-insights-grid">
          {/* Strengths */}
          <div className="air-insight-card air-insight-card--strength">
            <h3><Star size={16} /> Strengths</h3>
            <ul>
              {(r.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>

          {/* Weaknesses */}
          <div className="air-insight-card air-insight-card--weakness">
            <h3><AlertTriangle size={16} /> Areas to Improve</h3>
            <ul>
              {(r.weaknesses || []).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>

          {/* Suggestions */}
          <div className="air-insight-card air-insight-card--suggestion">
            <h3><Lightbulb size={16} /> Suggestions</h3>
            <ul>
              {(r.suggestions || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>

          {/* Daily Tasks */}
          <div className="air-insight-card air-insight-card--tasks">
            <h3><ListChecks size={16} /> Suggested Daily Tasks</h3>
            <ol>
              {(r.dailyTasks || []).map((t, i) => <li key={i}>{t}</li>)}
            </ol>
          </div>
        </div>

        {/* AI Feedback */}
        {r.aiFeedback && (
          <div className="air-feedback">
            <h3><Brain size={16} /> Detailed AI Feedback</h3>
            <p>{r.aiFeedback}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIReportPage;
